import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'taskCNs'

const initialState = {
	entities: {},
	ids: [],
	recents: [],
	upcoming: [],
	upcomingInitialized: false,
	isLoading: false,
	isInitialized: false,
	recentsInitialized: false,
	error: null,
	selectedTask: null,
	// Filter state
	searchQuery: '',
	filterPriority: '',
	filterProject: '',
	filterAssignee: '',
	showCompleted: false,
	paginationEnabled: false,
}

function prepConfig(getState) {
	const {
		userLogin: { userInfo },
	} = getState()

	return {
		headers: {
			Authorization: `Bearer ${userInfo.token}`,
		},
	}
}

// CREATE
export const createTaskCN = createAsyncThunk(
	`${SLICE_NAME}/createTask`,
	async (task: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/taskcns', task, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to create task')
		}
	}
)

// LOAD TEMPLATE into TaskCNs
export const loadTemplateCN = createAsyncThunk(
	`${SLICE_NAME}/loadTemplate`,
	async (template: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/taskcns/template', template, prepConfig(getState))
			return data
		} catch (error: any) {
			return rejectWithValue(error.response?.data?.message || 'Failed to load template')
		}
	}
)

// READ - all user tasks
export const initializeTaskCNs = createAsyncThunk(
	`${SLICE_NAME}/initialize`,
	async (payload: { userid: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/taskcns', {
				params: { userid: payload.userid },
				...prepConfig(getState),
			})
			return data
		} catch (error) {
			return rejectWithValue('Failed to initialize tasks')
		}
	}
)

// READ - assigned tasks
export const initializeAssignedTaskCNs = createAsyncThunk(
	`${SLICE_NAME}/myassigned`,
	async (payload: { assignedTo: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/taskcns/myassigned', {
				params: { assignedTo: payload.assignedTo },
				...prepConfig(getState),
			})
			return data
		} catch (error) {
			return rejectWithValue('Failed to fetch assigned tasks')
		}
	}
)

// READ - single task
export const getTaskCNById = createAsyncThunk(
	`${SLICE_NAME}/getById`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/taskcns/${id}`, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to fetch task')
		}
	}
)

// READ - tasks by project
export const getTaskCNsByProject = createAsyncThunk(
	`${SLICE_NAME}/getByProject`,
	async (payload: { projectid: string; userid: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/taskcns/mypackageid', {
				params: { projectid: payload.projectid, userid: payload.userid },
				...prepConfig(getState),
			})
			return data
		} catch (error) {
			return rejectWithValue('Failed to fetch project tasks')
		}
	}
)

// READ - tasks by portfolio
export const getTaskCNsByPortfolio = createAsyncThunk(
	`${SLICE_NAME}/getByPortfolio`,
	async (payload: { portfolioid: string; userid: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/taskcns/myportfolioid', {
				params: { portfolioid: payload.portfolioid, userid: payload.userid },
				...prepConfig(getState),
			})
			return data
		} catch (error) {
			return rejectWithValue('Failed to fetch portfolio tasks')
		}
	}
)

// READ - upcoming assigned tasks (next 2 weeks)
export const getUpcomingTaskCNs = createAsyncThunk(
	`${SLICE_NAME}/upcoming`,
	async (payload: { userid: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/taskcns/upcoming', {
				params: { userid: payload.userid },
				...prepConfig(getState),
			})
			return data
		} catch (error) {
			return rejectWithValue('Failed to fetch upcoming tasks')
		}
	}
)

// READ - recent tasks
export const initializeRecentTaskCNs = createAsyncThunk(
	`${SLICE_NAME}/myrecent`,
	async (payload: { userid: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/taskcns/myrecent', {
				params: { userid: payload.userid },
				...prepConfig(getState),
			})
			return data
		} catch (error) {
			return rejectWithValue('Failed to fetch recent tasks')
		}
	}
)

// UPDATE
export const updateTaskCN = createAsyncThunk(
	`${SLICE_NAME}/updateTask`,
	async (task: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(`/api/taskcns/${task._id}`, task, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to update task')
		}
	}
)

// CHECK DELETED DUPLICATE (single)
export const checkDeletedDuplicateTask = createAsyncThunk(
	`${SLICE_NAME}/checkDeletedDuplicate`,
	async (payload: { taskName: string; projectid: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/taskcns/check-deleted-duplicate', payload, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to check for deleted duplicates')
		}
	}
)

// CHECK DELETED DUPLICATES (batch, for templates)
export const checkDeletedDuplicateTasks = createAsyncThunk(
	`${SLICE_NAME}/checkDeletedDuplicates`,
	async (payload: { names?: string[]; templateId?: string; projectid: string }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/taskcns/check-deleted-duplicates', payload, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to check for deleted duplicates')
		}
	}
)

// RESTORE
export const restoreTaskCN = createAsyncThunk(
	`${SLICE_NAME}/restoreTask`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(`/api/taskcns/${id}/restore`, {}, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to restore task')
		}
	}
)

// DELETE
export const deleteTaskCN = createAsyncThunk(
	`${SLICE_NAME}/deleteTask`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(`/api/taskcns/${id}/delete`, {}, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to delete task')
		}
	}
)

// REORDER - bulk update `order` for a per-day list of tasks (drag-and-drop).
// The slice applies an optimistic update on `pending` so the UI doesn't flicker
// while the request is in flight; on `rejected` the list is marked stale so
// the consuming screen can refetch and self-heal.
export const reorderTaskCNs = createAsyncThunk(
	`${SLICE_NAME}/reorder`,
	async (taskIds: string[], { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put('/api/taskcns/reorder', { taskIds }, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to reorder tasks')
		}
	}
)

const taskCNSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		setSearchQuery: (state, action) => {
			state.searchQuery = action.payload
		},
		setFilterPriority: (state, action) => {
			state.filterPriority = action.payload
		},
		setFilterProject: (state, action) => {
			state.filterProject = action.payload
		},
		setFilterAssignee: (state, action) => {
			state.filterAssignee = action.payload
		},
		setShowCompleted: (state, action) => {
			state.showCompleted = action.payload
		},
		setPaginationEnabled: (state, action) => {
			state.paginationEnabled = action.payload
		},
		setSelectedTask: (state, action) => {
			state.selectedTask = action.payload
		},
		clearFilters: state => {
			state.searchQuery = ''
			state.filterPriority = ''
			state.filterProject = ''
			state.filterAssignee = ''
			state.showCompleted = false
		},
	},
	extraReducers: builder => {
		// Initialize all tasks
		builder
			.addCase(initializeTaskCNs.pending, state => {
				state.isLoading = true
			})
			.addCase(initializeTaskCNs.fulfilled, (state, action) => {
				const tasks = action.payload
				state.entities = {}
				state.ids =
					tasks?.map((task: any) => {
						state.entities[task._id] = task
						return task._id
					}) || []
				state.isLoading = false
				state.isInitialized = true
			})
			.addCase(initializeTaskCNs.rejected, (state, action) => {
				state.isLoading = false
				state.isInitialized = true
				state.error = action.error.message || null
			})

		// Initialize assigned tasks
		builder.addCase(initializeAssignedTaskCNs.fulfilled, (state, action) => {
			const tasks = action.payload
			state.entities = {}
			state.ids =
				tasks?.map((task: any) => {
					state.entities[task._id] = task
					return task._id
				}) || []
			state.isLoading = false
			state.isInitialized = true
		})

		// Get by project
		builder.addCase(getTaskCNsByProject.fulfilled, (state, action) => {
			const tasks = action.payload
			state.entities = {}
			state.ids =
				tasks?.map((task: any) => {
					state.entities[task._id] = task
					return task._id
				}) || []
			state.isLoading = false
			state.isInitialized = true
		})

		// Get by portfolio
		builder.addCase(getTaskCNsByPortfolio.fulfilled, (state, action) => {
			const tasks = action.payload
			state.entities = {}
			state.ids =
				tasks?.map((task: any) => {
					state.entities[task._id] = task
					return task._id
				}) || []
			state.isLoading = false
			state.isInitialized = true
		})

		// Get single task
		builder.addCase(getTaskCNById.fulfilled, (state, action) => {
			const task = action.payload
			if (task) {
				state.entities[task._id] = task
				state.selectedTask = task
			}
		})

		// Initialize recent tasks
		builder
			.addCase(initializeRecentTaskCNs.pending, state => {
				state.isLoading = true
			})
			.addCase(initializeRecentTaskCNs.fulfilled, (state, action) => {
				const tasks = action.payload

				// Filter out invalid entries and sort by updatedAt in reverse chronological order (most recent first)
				const validTasks = (tasks || []).filter(t => t && t._id)
				const sortedTasks = [...validTasks].sort((a, b) => {
					const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime()
					const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime()
					return dateB - dateA
				})

				sortedTasks.forEach((task: any) => {
					state.entities[task._id] = task
				})
				state.recents = sortedTasks.map((task: any) => task._id)
				state.isLoading = false
				state.recentsInitialized = true
			})
			.addCase(initializeRecentTaskCNs.rejected, (state, action) => {
				state.isLoading = false
				state.recentsInitialized = true
				state.error = action.error.message || null
			})

		// Upcoming tasks (next 2 weeks)
		builder
			.addCase(getUpcomingTaskCNs.fulfilled, (state, action) => {
				const tasks = action.payload
				tasks?.forEach((task: any) => {
					state.entities[task._id] = task
				})
				state.upcoming = tasks?.map((task: any) => task._id) || []
				state.upcomingInitialized = true
			})

		// Create
		builder.addCase(createTaskCN.fulfilled, (state, action) => {
			state.isInitialized = false
		})

		// Update -- optimistic on `pending` so inline edits in the detail sheet
		// (especially controlled inputs like the "Set specific time" switch)
		// don't flicker back to the old value during the ~200ms API round-trip.
		// Server-returned values on `fulfilled` are authoritative.
		builder
			.addCase(updateTaskCN.pending, (state, action) => {
				const patch = action.meta.arg as any
				if (patch && patch._id && state.entities[patch._id]) {
					state.entities[patch._id] = { ...state.entities[patch._id], ...patch }
					if (state.selectedTask && state.selectedTask._id === patch._id) {
						state.selectedTask = { ...state.selectedTask, ...patch }
					}
				}
			})
			.addCase(updateTaskCN.fulfilled, (state, action) => {
				const task = action.payload
				if (task && task._id) {
					state.entities[task._id] = task
					if (state.selectedTask && state.selectedTask._id === task._id) {
						state.selectedTask = task
					}
				}
			})

		// Restore
		builder.addCase(restoreTaskCN.fulfilled, (state, action) => {
			state.isInitialized = false
		})

		// Delete
		builder.addCase(deleteTaskCN.fulfilled, (state, action) => {
			state.isInitialized = false
			state.selectedTask = null
		})

		// Reorder (drag-and-drop within a day) -- optimistic
		builder
			.addCase(reorderTaskCNs.pending, (state, action) => {
				const taskIds = action.meta.arg as string[]
				taskIds.forEach((id, idx) => {
					if (state.entities[id]) {
						state.entities[id] = { ...state.entities[id], order: idx }
					}
				})
			})
			.addCase(reorderTaskCNs.fulfilled, (state, action) => {
				const tasks = action.payload as any[]
				tasks?.forEach(task => {
					if (task && task._id) state.entities[task._id] = task
				})
			})
			.addCase(reorderTaskCNs.rejected, (state, action) => {
				// Mark stale so the consumer can refetch and self-heal the order.
				state.isInitialized = false
				state.error = (action.payload as string) || 'Failed to reorder tasks'
			})
	},
})

export const {
	setSearchQuery,
	setFilterPriority,
	setFilterProject,
	setFilterAssignee,
	setShowCompleted,
	setPaginationEnabled,
	setSelectedTask,
	clearFilters,
} = taskCNSlice.actions

export default taskCNSlice
